import React from 'react';
import PropTypes from 'prop-types';

/**
 * ConversationListItem — Single conversation row in the list panel.
 *
 * Displays avatar, sender name, preview text with status-dependent styling,
 * and an unread indicator dot.
 */
function ConversationListItem({ conversation, isSelected, onClick }) {
  const { sender, lastMessage, unread, status, isPaidRequest, canSend, isCreator } = conversation;

  const getPreviewClass = () => {
    switch (status) {
      case 'closed':
        return 'msg-list-item__preview--closed';
      case 'failed':
        return 'msg-list-item__preview--failed';
      case 'pending':
        return 'msg-list-item__preview--waiting';
      case 'new':
        return '';
      default:
        if (lastMessage === 'You replied.') {
          return 'msg-list-item__preview--replied';
        }
        return '';
    }
  };

  return (
    <button
      type="button"
      className={`msg-list-item ${isSelected ? 'msg-list-item--selected' : ''}`}
      onClick={onClick}
      aria-label={`Conversation with ${sender.name}`}
    >
      <div className="msg-list-item__avatar-wrapper">
        <img
          src={sender.avatar}
          alt=""
          className="msg-list-item__avatar"
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
        <span className="msg-list-item__avatar-fallback" style={{ display: 'none' }}>
          {sender.name.charAt(0).toUpperCase()}
        </span>
        {unread && <span className="msg-list-item__unread-dot" aria-label="Unread" />}
        {sender.isOnline && <span className="msg-list-item__online-dot" />}
      </div>

      <div className="msg-list-item__content">
        <div className="msg-list-item__header">
          <span className="msg-list-item__name">{sender.name}</span>
          {isPaidRequest && (
            <span className="msg-list-item__request-badge">REQUEST</span>
          )}
        </div>
        <p className={`msg-list-item__preview ${getPreviewClass()}`}>
          {status === 'new' ? (
            'Ready to send your first message.'
          ) : status === 'pending' ? (
            canSend && isCreator ? 'New request' : 'Waiting for reply...'
          ) : status === 'closed' ? (
            'The chat has ended.'
          ) : status === 'failed' ? (
            'Creator failed to respond.'
          ) : (
            lastMessage
          )}
        </p>
      </div>
    </button>
  );
}

ConversationListItem.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sender: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      avatar: PropTypes.string.isRequired,
      isOnline: PropTypes.bool,
    }).isRequired,
    lastMessage: PropTypes.string.isRequired,
    unread: PropTypes.bool,
    status: PropTypes.string.isRequired,
    isPaidRequest: PropTypes.bool,
    canSend: PropTypes.bool,
    isCreator: PropTypes.bool,
  }).isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export default ConversationListItem;
